/**
 * shared/engines/readinessScore.js — Công cụ #2: điểm sẵn sàng gọi vốn.
 *
 * Đây là engine đầu tiên KHÔNG chứa nghiệp vụ. Toàn bộ tiêu chí, trọng số,
 * ngưỡng và lời khuyên nằm trong rubric (dữ liệu). Engine chỉ biết cách chạy
 * một rubric bất kỳ.
 *
 * Hệ quả: sửa trọng số hay thêm tiêu chí là sửa document Firestore, không phải
 * build lại app. `fundMatch` (#1) và phần chấm điểm của AI review sau này dùng
 * chung khuôn này.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * BA NGUYÊN TẮC
 *
 * 1. Không tự tính số phái sinh. Runway do engine runway tính rồi truyền vào
 *    qua `derived`. Nếu engine này tự tính lại, sẽ có hai công thức runway.
 *
 * 2. Phân biệt "chưa đạt" và "chưa điền". Điểm giống nhau (đều 0) nhưng lời
 *    khuyên khác hẳn, và độ tin cậy của tổng điểm cũng khác. Trả về `coverage`
 *    để người dùng biết điểm này dựa trên bao nhiêu phần dữ liệu.
 *
 * 3. Đầu ra quan trọng nhất không phải con số mà là `nextActions` — làm việc
 *    gì trước thì được nhiều điểm nhất. Một con số không kèm việc phải làm chỉ
 *    khiến founder lo thêm.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * HÀM THUẦN.
 */

import { apDung, chamThang, layGiaTri } from '../rules.js';

export const ENGINE_VERSION = '1.2.0';

const r1 = (n) => Math.round(n * 10) / 10;

/* ------------------------------------------------------------------ */
/* Chấm một tiêu chí                                                    */
/* ------------------------------------------------------------------ */

function chamTieuChi(tc, ctx) {
  const diemToiDa = Number(tc.points) || 0;

  // Tiêu chí thang bậc: chấm theo mức độ thay vì đạt/không đạt.
  if (Array.isArray(tc.scale)) {
    const giaTri = layGiaTri(ctx, tc.scalePath);
    const kq = chamThang(tc.scale, giaTri);
    return {
      key: tc.key,
      label: tc.label,
      max: diemToiDa,
      earned: kq.points,
      status: !kq.coThongTin ? 'CHUA_DIEN'
        : kq.points >= diemToiDa ? 'DAT'
          : kq.points > 0 ? 'MOT_PHAN' : 'CHUA_DAT',
      value: giaTri ?? null,
      whyItMatters: tc.whyItMatters,
      howToFix: tc.howToFix,
    };
  }

  const kq = apDung(tc.rule, ctx);

  // Điểm một phần: tài liệu mới soạn nháp, thoả thuận đã thống nhất nhưng chưa
  // ký. Nếu chỉ có đạt/không đạt thì người đã làm được nửa việc và người chưa
  // bắt đầu nhận cùng số điểm — và người thứ nhất sẽ thấy công cụ vô lý.
  if (!kq.dat && tc.partialRule) {
    const mp = apDung(tc.partialRule, ctx);
    if (mp.dat) {
      return {
        key: tc.key,
        label: tc.label,
        max: diemToiDa,
        earned: Number(tc.partialPoints) || Math.round(diemToiDa / 2),
        status: 'MOT_PHAN',
        value: kq.giaTri ?? null,
        whyItMatters: tc.whyItMatters,
        howToFix: tc.howToFix,
      };
    }
  }

  return {
    key: tc.key,
    label: tc.label,
    max: diemToiDa,
    earned: kq.dat ? diemToiDa : 0,
    status: kq.dat ? 'DAT' : kq.coThongTin ? 'CHUA_DAT' : 'CHUA_DIEN',
    value: kq.giaTri ?? null,
    whyItMatters: tc.whyItMatters,
    howToFix: tc.howToFix,
  };
}

/* ------------------------------------------------------------------ */
/* Cờ                                                                   */
/* ------------------------------------------------------------------ */

function chayCo(rubric, ctx) {
  const out = [];
  for (const co of rubric.flags || []) {
    const kq = apDung(co.rule, ctx);
    // `dao: true` nghĩa là cờ bật khi luật KHÔNG đạt (ví dụ "chưa có bằng chứng
    // nào" là phủ định của "có ít nhất 1 bằng chứng").
    const bat = co.dao ? !kq.dat : kq.dat;
    // Cờ chỉ bật khi có đủ thông tin để kết luận — không doạ người dùng vì họ
    // chưa kịp điền ô nào đó.
    if (bat && kq.coThongTin) {
      out.push({
        code: co.code,
        severity: co.severity,
        message: co.message,
        fix: co.fix,
      });
    }
  }
  const order = { HIGH: 0, MEDIUM: 1, LOW: 2 };
  return out.sort((a, b) => order[a.severity] - order[b.severity]);
}

/* ------------------------------------------------------------------ */
/* Điểm vào chính                                                       */
/* ------------------------------------------------------------------ */

/**
 * @param {Object} input
 * @param {Object} input.profile  - ventures/{id}.profile
 * @param {Object} input.rubric   - document rubric (xem shared/rubrics/)
 * @param {Object} [input.derived] - số phái sinh do engine khác tính,
 *                                   ví dụ { runwayMonths: 7.2 }
 * @param {Object} [input.input]   - dữ liệu riêng của công cụ (inputs/{toolId})
 */
export function evaluate(input) {
  if (!input || !input.rubric) throw new Error('Cần truyền rubric');
  const { rubric } = input;
  if (!Array.isArray(rubric.axes) || rubric.axes.length === 0) {
    throw new Error('Rubric phải có ít nhất một trục');
  }

  const tongTrongSo = rubric.axes.reduce((a, t) => a + (Number(t.weight) || 0), 0);
  if (tongTrongSo <= 0) throw new Error('Tổng trọng số các trục phải lớn hơn 0');

  // Ba không gian tên cho đường dẫn trong rubric:
  //   profile.*  hồ sơ doanh nghiệp (dùng chung cho mọi rubric)
  //   derived.*  số phái sinh do engine khác tính (runway, mrr…)
  //   input.*    dữ liệu riêng của một công cụ, ví dụ trạng thái từng slide
  //              trong ventures/{id}/inputs/pitch-deck
  const ctx = {
    profile: input.profile || {},
    derived: input.derived || {},
    input: input.input || {},
  };

  let daDanhGia = 0;
  let tongTieuChi = 0;
  let diemTong = 0;

  const axes = rubric.axes.map((truc) => {
    const criteria = (truc.criteria || []).map((tc) => chamTieuChi(tc, ctx));
    const max = criteria.reduce((a, c) => a + c.max, 0);
    const earned = criteria.reduce((a, c) => a + c.earned, 0);

    tongTieuChi += criteria.length;
    daDanhGia += criteria.filter((c) => c.status !== 'CHUA_DIEN').length;

    const trongSo = Number(truc.weight) || 0;
    // Quy về thang 100 theo trọng số trục, không cộng thẳng điểm thô — nếu
    // cộng thô thì trục nào nhiều tiêu chí sẽ tự nhiên nặng hơn.
    const gopVaoTong = max > 0 ? (earned / max) * (trongSo / tongTrongSo) * 100 : 0;
    diemTong += gopVaoTong;

    return {
      key: truc.key,
      label: truc.label,
      weight: trongSo,
      earned,
      max,
      pct: max > 0 ? r1((earned / max) * 100) : null,
      contribution: r1(gopVaoTong),
      criteria,
    };
  });

  const score = Math.round(diemTong);
  const band = (rubric.bands || []).find((b) => score >= b.min)
    || { key: 'KHONG_XAC_DINH', label: 'Chưa xác định' };

  /**
   * Việc nên làm trước: sắp theo số điểm lấy lại được nếu xử lý xong.
   * Tiêu chí chưa điền vẫn vào danh sách nhưng xếp sau — điền ô thì nhanh, còn
   * ký vesting hay xin thư cam kết mới là việc thật.
   */
  const nextActions = [];
  for (const truc of axes) {
    for (const c of truc.criteria) {
      if (c.status === 'DAT') continue;
      const thieu = c.max - c.earned;
      const loiIch = truc.max > 0 ? (thieu / truc.max) * (truc.weight / tongTrongSo) * 100 : 0;
      nextActions.push({
        axisKey: truc.key,
        axisLabel: truc.label,
        key: c.key,
        label: c.label,
        status: c.status,
        pointsAvailable: r1(loiIch),
        // Điểm tổng nếu chỉ xử lý xong riêng việc này. Tính từ tổng chưa làm
        // tròn để "+6 điểm, lên 79" khớp với cách làm tròn của `score`.
        scoreAfter: Math.min(100, Math.round(diemTong + loiIch)),
        whyItMatters: c.whyItMatters,
        howToFix: c.howToFix,
      });
    }
  }
  nextActions.sort((a, b) => {
    if (a.status === 'CHUA_DIEN' && b.status !== 'CHUA_DIEN') return 1;
    if (b.status === 'CHUA_DIEN' && a.status !== 'CHUA_DIEN') return -1;
    return b.pointsAvailable - a.pointsAvailable;
  });

  /**
   * Một việc nên sửa tiếp theo (kiểu "Fix next"): việc thật có nhiều điểm nhất.
   * Ưu tiên việc đã điền mà chưa đạt; chỉ khi không còn việc nào như vậy mới
   * nhắc tới ô chưa điền. Không còn việc gì thì null.
   */
  const nextBest = nextActions.find((a) => a.status !== 'CHUA_DIEN' && a.pointsAvailable > 0)
    || nextActions.find((a) => a.pointsAvailable > 0)
    || null;

  return {
    engineVersion: ENGINE_VERSION,
    rubricId: rubric.rubricId,
    rubricVersion: rubric.version,
    score,
    band: band.key,
    bandLabel: band.label,
    axes,
    coverage: {
      assessed: daDanhGia,
      total: tongTieuChi,
      pct: tongTieuChi > 0 ? r1((daDanhGia / tongTieuChi) * 100) : 0,
      confidence: daDanhGia / Math.max(tongTieuChi, 1) >= 0.8 ? 'HIGH'
        : daDanhGia / Math.max(tongTieuChi, 1) >= 0.5 ? 'MEDIUM' : 'LOW',
    },
    nextActions,
    nextBest,
    flags: chayCo(rubric, ctx),
  };
}

export default { ENGINE_VERSION, evaluate };
